import { Avatar, Select, SelectItem } from "@heroui/react";
import { AlertTriangle, Target } from "lucide-react";
import { GlowEffect } from "@/components/global-ui/glow-effect";
import { useState } from "react";
import { Id } from "@/convex/_generated/dataModel";

// Dummy personas data to match the template generator
const dummyPersonas = [{
  _id: "persona1" as Id<"personas">,
  _creationTime: Date.now(),
  orgId: "org1",
  name: "Marketing Manager",
  job_title: "Digital Marketing Manager",
  industry: "SaaS",
  challenges: ["Limited budget for campaigns", "Need to prove ROI"],
  painPoints: ["Struggles with content creation efficiency"],
  demographics: "25-35, Tech-savvy professional",
  goals: "Increase brand awareness and lead generation",
  behaviors: "Active on social media, data-driven decisions",
  siteId: "site1" as Id<"sites">,
  story: {
    background: "A digital marketing manager at a growing SaaS company, responsible for driving user acquisition and brand awareness through content marketing and social media campaigns.",
    vision_for_success: "Wants to create viral content that significantly increases brand visibility and drives qualified leads to the sales team."
  },
  problem_layers: ["Limited budget for paid advertising campaigns", "Difficulty proving ROI on content marketing efforts", "Struggle to create engaging content consistently"],
  benefits: ["Viral content that drives organic growth", "Clear attribution of leads to marketing efforts", "Streamlined content creation process"]
}, {
  _id: "persona2" as Id<"personas">,
  _creationTime: Date.now(),
  orgId: "org1",
  name: "Startup Founder",
  job_title: "CEO & Founder",
  industry: "Tech Startup",
  challenges: ["Building brand from scratch", "Limited marketing team"],
  painPoints: ["Needs viral content to gain traction"],
  demographics: "28-40, Entrepreneur",
  goals: "Build product awareness and user base",
  behaviors: "Networking, content creation, growth hacking",
  siteId: "site1" as Id<"sites">,
  story: {
    background: "A startup founder building a revolutionary tech product, wearing multiple hats including marketing, sales, and product development while trying to gain market traction.",
    vision_for_success: "Aims to achieve product-market fit and build a sustainable user base through authentic storytelling and viral marketing."
  },
  problem_layers: ["Building brand recognition in competitive market", "Limited resources for traditional marketing", "Need to wear multiple hats and prioritize tasks"],
  benefits: ["Viral growth that reduces customer acquisition costs", "Strong brand awareness in target market", "Community of engaged early adopters"]
}, {
  _id: "persona3" as Id<"personas">,
  _creationTime: Date.now(),
  orgId: "org1",
  name: "Content Creator",
  job_title: "Social Media Content Creator",
  industry: "Media & Entertainment",
  challenges: ["Consistent content creation", "Standing out in crowded space"],
  painPoints: ["Running out of creative ideas"],
  demographics: "22-30, Creative professional",
  goals: "Grow follower base and engagement",
  behaviors: "Trend watching, community building",
  siteId: "site1" as Id<"sites">,
  story: {
    background: "A creative professional who builds engaging content for social media platforms, constantly searching for new ways to connect with audiences and grow their personal brand.",
    vision_for_success: "Wants to become a recognized thought leader in their niche with a highly engaged community and multiple revenue streams."
  },
  problem_layers: ["Constantly needing fresh, creative content ideas", "Standing out in an oversaturated market", "Building genuine engagement beyond vanity metrics"],
  benefits: ["Never-ending stream of viral content ideas", "Unique voice that cuts through the noise", "Authentic community of engaged followers"]
}];

// Persona Generator Component
export default function PersonaGeneratorPreview() {
  const [selectedPersonaId, setSelectedPersonaId] = useState<string>(dummyPersonas[0]._id);

  // Get the currently selected persona
  const selectedPersona = dummyPersonas.find(p => p._id === selectedPersonaId) || dummyPersonas[0];
  return <div className="group relative h-full w-full" data-sentry-component="PersonaGeneratorPreview" data-sentry-source-file="persona-generator-preview.tsx">
      {/* Center Select Section For Selecting Persona */}
      <div className="absolute inset-0 z-50 flex items-center justify-center pb-28">
        <div className="relative w-full max-w-xs">
          <GlowEffect colors={["#f97316", "#c2410c", "#fb923c", "#fca5a5", "#ef4444"]} sides={["top"]} mode="colorShift" blur="soft" duration={3} className="px-auto mx-auto mt-0.5 w-[85%] rounded-lg" data-sentry-element="GlowEffect" data-sentry-source-file="persona-generator-preview.tsx" />
          <Select label="Select Persona" placeholder="Select Persona" selectedKeys={[selectedPersonaId]} onSelectionChange={keys => {
          const selected = Array.from(keys)[0] as string;
          if (selected) {
            setSelectedPersonaId(selected);
          }
        }} className="relative z-10 w-full" classNames={{
          base: "dark",
          // Apply dark mode context to the entire component
          popoverContent: "dark text-white" // Dropdown menu background and text
          //   label: "text-white/80", // Mimic dark mode label color
          //   trigger: "bg-gray-800 text-white hover:bg-gray-700", // Dark mode background and text
          //   innerWrapper: "bg-gray-800", // Inner wrapper background
          //   listbox: "bg-gray-900", // Listbox background
          //   selectorIcon: "text-white/80", // Icon color
        }} data-sentry-element="Select" data-sentry-source-file="persona-generator-preview.tsx">
            {dummyPersonas.map(persona => <SelectItem key={persona._id} textValue={persona.name}>
                <div className="flex items-center gap-2">
                  <Avatar alt={persona.name} className="flex-shrink-0" size="sm" src={`https://api.dicebear.com/7.x/personas/svg?seed=${persona.name}`} />
                  <div className="flex flex-col">
                    <span className="text-small">{persona.name}</span>
                    <span className="text-tiny text-default-400">
                      {persona.job_title}
                    </span>
                  </div>
                </div>
              </SelectItem>)}
          </Select>
        </div>
      </div>

      <div className="space-y-4 p-4">
        {/* Hero Section */}
        <div className="flex items-center gap-4 opacity-5 transition-opacity duration-700 group-hover:opacity-15">
          <Avatar className="h-16 w-16" src={`https://api.dicebear.com/7.x/personas/svg?seed=${selectedPersona.name}`} fallback={<span className="text-lg text-white">
                {selectedPersona.name[0]}
              </span>} data-sentry-element="Avatar" data-sentry-source-file="persona-generator-preview.tsx" />
          <div>
            <h3 className="text-lg font-semibold text-white">
              {selectedPersona.name}
            </h3>
            <p className="text-sm text-gray-400">{selectedPersona.job_title}</p>
            <p className="text-xs text-gray-400">{selectedPersona.industry}</p>
          </div>
        </div>

        {/* Story Section */}
        <div className="space-y-2 opacity-5 transition-opacity duration-700 group-hover:opacity-15">
          <p className="text-xs leading-relaxed text-gray-300">
            {selectedPersona.story.background}
          </p>
        </div>

        {/* Challenges Section */}
        <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-3 opacity-5 transition-opacity duration-700 group-hover:opacity-15">
          <div className="mb-2 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-400" data-sentry-element="AlertTriangle" data-sentry-source-file="persona-generator-preview.tsx" />
            <h4 className="text-sm font-medium text-white">Key Challenges</h4>
          </div>
          <ul className="space-y-1">
            {selectedPersona.problem_layers.slice(0, 2).map((item: string, index: number) => <li key={index} className="flex items-start gap-2 text-xs text-gray-300">
                  <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-gray-500" />
                  <span>{item}</span>
                </li>)}
          </ul>
        </div>

        {/* Goals Section */}
        <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-3 opacity-50 transition-opacity duration-700 group-hover:opacity-100">
          <div className="mb-2 flex items-center gap-2">
            <Target className="h-4 w-4 text-green-400" data-sentry-element="Target" data-sentry-source-file="persona-generator-preview.tsx" />
            <h4 className="text-sm font-medium text-white">Goals</h4>
          </div>
          <ul className="space-y-1">
            {selectedPersona.benefits.slice(0, 2).map((item: string, index: number) => <li key={index} className="flex items-start gap-2 text-xs text-gray-300">
                  <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-gray-500" />
                  <span>{item}</span>
                </li>)}
          </ul>
        </div>
      </div>

      {/* Overlay Gradient */}
      <div className="absolute inset-0 m-auto h-full w-full rounded-b-3xl bg-gradient-to-r from-black/50 via-transparent to-black/50" />

      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 w-full rounded-b-3xl bg-gradient-to-t from-black via-black to-transparent px-6 pb-6 pt-20">
        <div>
          <h3 className="text-xl font-bold text-white">Persona Generator</h3>
          <p className="z-40 text-gray-400">
            Create detailed customer personas in seconds with AI-powered
            insights
          </p>
        </div>
      </div>
    </div>;
}